import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router";
import Select from "react-select";
import { saveAs } from "file-saver";
import "./NewCards.css";
import { createCards, getScene } from "../../utils/api";
import { Link } from "react-router";
import { useScenes } from "../../context/ScenesContext";
import { generateCardsPDF } from "../../utils/generateCardsPDF";

export default function NewCards() {
  const [searchParams] = useSearchParams();
  const [selectedScene, setSelectedScene] = useState<string | null>(null);
  const [cardCount, setCardCount] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const { scenes } = useScenes();

  useEffect(() => {
    // Pre-select scene from URL params if provided
    const sceneIdParam = searchParams.get("scene_id");
    if (sceneIdParam && scenes && scenes.some(s => s.id === sceneIdParam)) {
      setSelectedScene(sceneIdParam);
    }
  }, [scenes, searchParams]);

  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: 46,
      borderRadius: 10,
      borderColor: state.isFocused ? "#d97706" : "#e7e5e4",
      boxShadow: state.isFocused ? "0 0 0 1px #d97706" : "none",
      background: "#ffffff"
    }),
    menu: (base: any) => ({
      ...base,
      border: "1px solid #e7e5e4",
      borderRadius: 10,
      overflow: "hidden"
    }),
    option: (base: any, state: any) => ({
      ...base,
      background: state.isSelected ? "#d97706" : state.isFocused ? "#fffbeb" : "#ffffff",
      color: state.isSelected ? "#ffffff" : "#1c1917"
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "#78716c"
    })
  };

  const handleGenerateCards = async () => {
    if (!selectedScene) {
      alert("Please select a scene.");
      return;
    }
    setIsGenerating(true);
    try {
      // Fetch the full scene to get card_image and qr_settings (not included in the scene list)
      const { scene } = await getScene(selectedScene, new Date(0), new Date());

      const qrSettings = scene.qr_settings;
      if (!qrSettings || Object.keys(qrSettings).length === 0) {
        alert("This scene has no QR settings configured yet. Please set up the QR settings in the Scene editor first.");
        return;
      }

      const generatedIds = await createCards(selectedScene, cardCount);

      // CSV download
      const csvContent = generatedIds.join("\n");
      saveAs(new Blob([csvContent], { type: "text/csv;charset=utf-8;" }), "generated_cards.csv");

      // PDF download
      await generateCardsPDF(
        generatedIds,
        scene.card_image ?? null,
        qrSettings,
        scene.scene_name ?? selectedScene,
      );
    } catch (error) {
      console.error("Error generating cards:", error);
      alert("Failed to generate cards. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="page-shell new-cards-container">
      <h1 className="page-title new-cards-header">New Cards</h1>
      <p className="page-subtitle">Generate card IDs for a scene and export them as a CSV file.</p>
      
      {scenes && scenes.length === 0 && (
        <div className="info-message">
          <p>No scenes available. You need to create a collection and scene first.</p>
          <Link to="/" className="btn-ark">Go to Home</Link>
        </div>
      )}
      
      {scenes && scenes.length > 0 && (
        <div className="new-cards-form">
          <label className="field-label" htmlFor="scene-select">
            Scene
          </label>
          <Select
            inputId="scene-select"
            classNamePrefix="ark-select"
            isClearable
            styles={selectStyles}
            value={scenes.find(s => s.id === selectedScene) ? { value: selectedScene, label: scenes.find(s => s.id === selectedScene)!.scene_name } : null}
            onChange={(value) => setSelectedScene(value?.value || null)}
            placeholder="Select a scene"
            options={scenes.map((scene) => ({
              value: scene.id,
              label: scene.scene_name,
            }))}
          />
          <label className="field-label" htmlFor="card-count-input">
            Number of cards
          </label>
          <input
            id="card-count-input"
            className="count-input"
            type="number"
            min={0}
            value={cardCount}
            onChange={(e) => setCardCount(Number(e.target.value))}
            placeholder="Number of cards"
          />
          <button className="btn-ark" onClick={handleGenerateCards} disabled={!selectedScene || cardCount <= 0 || isGenerating}>
            {isGenerating ? "Generating…" : "Generate Cards"}
          </button>
        </div>
      )}
    </div>
  );
}
